import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminLogin.css";
const API_BASE = process.env.REACT_APP_API_URL || "https://hnoelauto-backend-production.up.railway.app";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm]       = useState({ username: "", password: "" });
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let res;
      try {
        res = await fetch(`${API_BASE}/api/admin/login`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(form),
        });
      } catch (networkErr) {
        // fetch itself threw — server is likely not running or proxy is misconfigured
        throw new Error(
          "Cannot reach the server. Make sure the backend is running on port 3001 and your proxy is configured. (" +
          networkErr.message + ")"
        );
      }

      // Try to parse JSON regardless of status so we can show server error messages
      let data;
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        // Server returned HTML (e.g. React dev server 404 page) — proxy not hitting the API
        const text = await res.text();
        console.error("Non-JSON response from /api/admin/login:", text.slice(0, 300));
        throw new Error(
          `Server returned ${res.status} (non-JSON). ` +
          "Check that your backend server is running and REACT_APP_API_URL is set correctly."
        );
      }

      if (!res.ok) {
        throw new Error(data.message || `Login failed (HTTP ${res.status}).`);
      }

      // Validate the response shape before storing
      if (!data.token || !data.user) {
        console.error("Unexpected login response:", data);
        throw new Error("Unexpected response from server — missing token or user.");
      }

      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_user", JSON.stringify(data.user));

      // Small delay to ensure localStorage is flushed before navigation
      setTimeout(() => {
        navigate("/admin/dashboard");
      }, 50);

    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="al-root">
      {/* Left decorative panel */}
      <div className="al-panel">
        <div className="al-panel__bg" />
        <div className="al-panel__content">
          <div className="al-panel__logo">
            <span className="al-panel__logo-icon">⚙</span>
            <span className="al-panel__logo-text">NOELAUTO</span>
          </div>
          <h2 className="al-panel__headline">
            Admin<br /><span>Control</span><br />Centre
          </h2>
          <p className="al-panel__sub">
            Manage products, orders, customers,<br />
            services and site content.
          </p>
          <div className="al-panel__stripes" />
        </div>
      </div>

      {/* Right login form */}
      <div className="al-form-side">
        <div className="al-card">
          <div className="al-card__header">
            <div className="al-card__tag">ADMINISTRATOR</div>
            <h1 className="al-card__title">Sign In</h1>
            <p className="al-card__sub">Enter your credentials to continue</p>
          </div>

          {error && (
            <div className="al-error">
              <span className="al-error__icon">!</span>
              <span>{error}</span>
            </div>
          )}

          <form className="al-form" onSubmit={handleSubmit} autoComplete="off">
            <div className="al-field">
              <label className="al-label" htmlFor="username">Username</label>
              <div className="al-input-wrap">
                <span className="al-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  id="username"
                  name="username"
                  className="al-input"
                  type="text"
                  placeholder="Enter username"
                  value={form.username}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="al-field">
              <label className="al-label" htmlFor="password">Password</label>
              <div className="al-input-wrap">
                <span className="al-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  id="password"
                  name="password"
                  className="al-input"
                  type={showPw ? "text" : "password"}
                  placeholder="Enter password"
                  value={form.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="al-toggle-pw"
                  onClick={() => setShowPw((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              className={`al-btn${loading ? " al-btn--loading" : ""}`}
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <span className="al-spinner" />
              ) : (
                <>
                  Sign In
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <p className="al-card__footer">
            &copy; {new Date().getFullYear()} Noel Auto &mdash; Admin Portal
          </p>
        </div>
      </div>
    </div>
  );
}